// frontend/src/App.js

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Typography } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import theme from './theme/theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ManufacturingDataProvider } from './contexts/ManufacturingDataContext';
import Navbar from './components/Navbar';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import UserDashboard from './pages/UserDashboard';
import Profile from './pages/Profile';
import UserManagement from './pages/UserManagement';
import OrdersApiKeys from './pages/OrdersApiKeys';
import ManufacturingDashboard from './pages/manufacturing/ManufacturingDashboard'; 
import ItemManagement from './pages/manufacturing/ItemManagement';
import OperationManagement from './pages/manufacturing/OperationManagement';
import BOMManagement from './pages/manufacturing/BOMManagement';
import InventoryManagement from './pages/manufacturing/InventoryManagement';
import WorkOrderManagement from './pages/manufacturing/WorkOrderManagement';
import ShopFloorHome from './pages/shopfloor/ShopFloorHome';
import ShopFloorTerminal from './pages/shopfloor/ShopFloorTerminal';
import CustomerOrderManagement from './pages/manufacturing/CustomerOrderManagement';
import AdminScripts from './pages/AdminScripts';
import DataImport from './pages/manufacturing/DataImport';
import CustomerOrderDetail from './pages/manufacturing/CustomerOrderDetail';
import SageImport from './pages/manufacturing/SageImport';
import SageMappings from './pages/manufacturing/SageMappings';
import DXShippingPage from './pages/manufacturing/DXShippingPage';
import Settings from './pages/Settings';
import CustomerManagement from './pages/manufacturing/CustomerManagement';
import CustomerDetail from './pages/manufacturing/CustomerDetail';
import StockConversion from './pages/manufacturing/StockConversion';
import DispatchManagement from './pages/dispatch/DispatchManagement';
import DispatchOrderDetail from './pages/dispatch/DispatchOrderDetail';
import ShippingMethodSelection from './pages/dispatch/ShippingMethodSelection';
import ManualShipping from './pages/dispatch/ManualShipping';
import Reports from './pages/Reports';
import PlannedAdherence from './pages/PlannedAdherence';
import BOMComponentSwap from './pages/manufacturing/BOMComponentSwap';
import HardboardBOMUpdate from './pages/manufacturing/HardboardBOMUpdate';
import { setConnectionErrorHandler } from './services/api';
import { ConnectionProvider, useConnection } from './components/ConnectionStatus';
import ManualWorkOrderCreation from './pages/manufacturing/ManualWorkOrderCreation';
import TVDashboard from './pages/TVDashboard';
import BOMConfigurator from './pages/BOMConfigurator';
import OrderReconciliation from './pages/manufacturing/OrderReconciliation';
import StockAllocationManagement from './pages/manufacturing/StockAllocationManagement';
import CapacityPlanning from './pages/CapacityPlanning';
import StockProcurement from './pages/StockProcurement';
import Backups from './pages/Backups';
import WorkOrderManagementV2 from './pages/manufacturing/v2/WorkOrderManagementV2';
import ShopFloorTerminalV2 from './pages/manufacturing/v2/ShopFloorTerminalV2';
import CreateWorkOrderV2 from './pages/manufacturing/v2/CreateWorkOrderV2';
import TransactionHistoryV2 from './pages/manufacturing/v2/TransactionHistoryV2';
import CapacityPlanningV2 from './pages/manufacturing/v2/CapacityPlanningV2';
import WOCreationLogV2 from './pages/manufacturing/v2/WOCreationLogV2';
import MaterialShortagesV2 from './pages/manufacturing/v2/MaterialShortagesV2';
import ScrapV2 from './pages/manufacturing/v2/ScrapV2';
import ManufacturingDashboardV2 from './pages/manufacturing/v2/ManufacturingDashboardV2';
import KPIDashboardV2 from './pages/manufacturing/v2/KPIDashboardV2';
import './App.css';

// Protected Route Component with page-level permissions
const ProtectedRoute = ({ children, adminOnly = false, requiredPage = null }) => {
  const { isAuthenticated, user, loading, hasPageAccess } = useAuth();
  
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }
  
  if (!isAuthenticated) {
    console.log('🔒 Not authenticated, redirecting to home');
    return <Navigate to="/" />;
  }
  
  if (adminOnly && !user?.is_staff && user?.role !== 'admin') {
    console.log('🔒 Admin-only route, user not admin, redirecting to dashboard');
    return <Navigate to="/dashboard" />;
  }
  
  // Check page-specific permissions
  if (requiredPage) {
    const hasAccess = hasPageAccess(requiredPage);
    console.log(`🔐 Checking access to '${requiredPage}': ${hasAccess}`);
    
    if (!hasAccess) {
      console.log(`🚫 Access denied to '${requiredPage}', redirecting to dashboard`);
      return <Navigate to="/dashboard" />;
    }
  }
  
  return children;
};

// Dashboard Router Component
const DashboardRouter = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_staff || user?.role === 'admin';
  
  return isAdmin ? <AdminDashboard /> : <UserDashboard />;
};

function AppRoutes() {
  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      backgroundAttachment: 'fixed',
      overflowX: 'hidden',
      maxWidth: '100vw'
    }}>
      <Navbar />
      <Box sx={{
        background: theme.palette.background.default,
        minHeight: 'calc(100vh - 64px)',
        overflowX: 'hidden',
        width: '100%'
      }}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Dashboard />} />
          
          {/* Dashboard Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute requiredPage="dashboard">
                <DashboardRouter />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/admin-dashboard" 
            element={
              <ProtectedRoute adminOnly requiredPage="admin-dashboard">
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* User Management - Admin Only */}
          <Route
            path="/users"
            element={
              <ProtectedRoute adminOnly requiredPage="users">
                <UserManagement />
              </ProtectedRoute>
            }
          />

          {/* Orders API keys - Admin Only */}
          <Route
            path="/orders-api"
            element={
              <ProtectedRoute adminOnly requiredPage="orders-api">
                <OrdersApiKeys />
              </ProtectedRoute>
            }
          />

          {/* Profile */}
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute requiredPage="profile">
                <Profile />
              </ProtectedRoute>
            } 
          />

          {/* Dispatch Routes */}
          <Route 
            path="/dispatch" 
            element={
              <ProtectedRoute requiredPage="dispatch">
                <ManufacturingDataProvider>
                  <DispatchManagement />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/dispatch/orders/:orderId" 
            element={
              <ProtectedRoute requiredPage="dispatch">
                <ManufacturingDataProvider>
                  <DispatchOrderDetail />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/dispatch/orders/:orderId/ship" 
            element={
              <ProtectedRoute requiredPage="dispatch">
                <ManufacturingDataProvider>
                  <ShippingMethodSelection />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/manufacturing/create-work-order" 
            element={
              <ProtectedRoute requiredPage="work-orders">
                <ManufacturingDataProvider>
                  <ManualWorkOrderCreation />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/manufacturing/allocations" 
            element={
              <ProtectedRoute requiredPage="allocations">
                <ManufacturingDataProvider>
                  <StockAllocationManagement />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/manufacturing/bom-configurator" 
            element={
              <ProtectedRoute requiredPage="bom-configurator">
                <ManufacturingDataProvider>
                  <BOMConfigurator />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/dispatch/orders/:orderId/manual" 
            element={
              <ProtectedRoute requiredPage="dispatch">
                <ManufacturingDataProvider>
                  <ManualShipping />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />
          
          {/* Manufacturing Routes */}
          <Route 
            path="/manufacturing" 
            element={
              <ProtectedRoute requiredPage="manufacturing">
                <ManufacturingDataProvider>
                  <ManufacturingDashboard />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/manufacturing/sage-import" 
            element={
              <ProtectedRoute requiredPage="sage-import">
                <SageImport />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/manufacturing/sage-mappings" 
            element={
              <ProtectedRoute requiredPage="sage-mappings">
                <SageMappings />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/manufacturing/operations" 
            element={
              <ProtectedRoute requiredPage="operations">
                <ManufacturingDataProvider>
                  <OperationManagement />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/manufacturing/customer-orders/:orderId/dx-shipping" 
            element={
              <ProtectedRoute requiredPage="dispatch">
                <ManufacturingDataProvider>
                  <DXShippingPage />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/manufacturing/bom" 
            element={
              <ProtectedRoute requiredPage="bom">
                <ManufacturingDataProvider>
                  <BOMManagement />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/tv-dashboard" 
            element={
              <ProtectedRoute requiredPage="tv-dashboard">
                <TVDashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/manufacturing/inventory" 
            element={
              <ProtectedRoute requiredPage="inventory">
                <ManufacturingDataProvider>
                  <InventoryManagement />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/manufacturing/work-orders" 
            element={
              <ProtectedRoute requiredPage="work-orders">
                <ManufacturingDataProvider>
                  <WorkOrderManagement />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/manufacturing/customer-orders" 
            element={
              <ProtectedRoute requiredPage="customer-orders">
                <ManufacturingDataProvider>
                  <CustomerOrderManagement />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/manufacturing/customer-orders/:orderId" 
            element={
              <ProtectedRoute requiredPage="customer-orders">
                <ManufacturingDataProvider>
                  <CustomerOrderDetail />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/manufacturing/items" 
            element={
              <ProtectedRoute requiredPage="items">
                <ManufacturingDataProvider>
                  <ItemManagement />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/manufacturing/recalculate" 
            element={
              <ProtectedRoute requiredPage="recalculate">
                <ManufacturingDataProvider>
                  <OrderReconciliation />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />
                    
          {/* Shop Floor Routes */}
          <Route 
            path="/shop-floor" 
            element={
              
              <ShopFloorHome />
              
            } 
          />
          
          <Route 
            path="/shop-floor/:operationCode" 
            element={
              
              <ShopFloorTerminal />
              
            } 
          />
          
          {/* Data Import */}
          <Route 
            path="/import" 
            element={
              <ProtectedRoute requiredPage="import">
                <DataImport />
              </ProtectedRoute>
            } 
          />
          
          {/* Settings */}
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute requiredPage="settings">
                <Settings />
              </ProtectedRoute>
            } 
          />
          
          {/* Analytics */}
          <Route
            path="/analytics"
            element={
              <ProtectedRoute requiredPage="analytics">
                <PlannedAdherence />
              </ProtectedRoute>
            }
          />
          
          {/* Reports */}
          <Route 
            path="/reports" 
            element={
              <ProtectedRoute requiredPage="reports">
                <Reports />
              </ProtectedRoute>
            } 
          />
          
          {/* Tasks */}
          <Route 
            path="/tasks" 
            element={
              <ProtectedRoute requiredPage="tasks">
                <Box sx={{ p: 4 }}>
                  <Typography variant="h4">Tasks (Coming Soon)</Typography>
                </Box>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/manufacturing/customers" 
            element={
              <ProtectedRoute requiredPage="customers">
                <ManufacturingDataProvider>
                  <CustomerManagement />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />

          <Route
            path="/manufacturing/bom-swap"
            element={
              <ProtectedRoute requiredPage="bom-swap">
                <ManufacturingDataProvider>
                  <BOMComponentSwap />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/hardboard-bom"
            element={
              <ProtectedRoute adminOnly>
                <HardboardBOMUpdate />
              </ProtectedRoute>
            }
          />

          <Route 
            path="/manufacturing/customers/:customerId" 
            element={
              <ProtectedRoute requiredPage="customers">
                <ManufacturingDataProvider>
                  <CustomerDetail />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/manufacturing/convert" 
            element={
              <ProtectedRoute requiredPage="convert">
                <ManufacturingDataProvider>
                  <StockConversion />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            } 
          />
          
          {/* Manufacturing V2 (Beta) — parallel module */}
          <Route
            path="/manufacturing/v2/work-orders"
            element={
              <ProtectedRoute requiredPage="work-orders-v2">
                <ManufacturingDataProvider>
                  <WorkOrderManagementV2 />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manufacturing/v2/work-orders/new"
            element={
              <ProtectedRoute requiredPage="work-orders-v2-create">
                <ManufacturingDataProvider>
                  <CreateWorkOrderV2 />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manufacturing/v2/shop-floor"
            element={
              <ProtectedRoute requiredPage="shop-floor-v2">
                <ManufacturingDataProvider>
                  <ShopFloorTerminalV2 />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manufacturing/v2/shop-floor/:operationCode"
            element={
              <ProtectedRoute requiredPage="shop-floor-v2">
                <ManufacturingDataProvider>
                  <ShopFloorTerminalV2 />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manufacturing/v2/history"
            element={
              <ProtectedRoute requiredPage="transaction-history-v2">
                <ManufacturingDataProvider>
                  <TransactionHistoryV2 />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manufacturing/v2/wo-creation-log"
            element={
              <ProtectedRoute requiredPage="wo-creation-log">
                <ManufacturingDataProvider>
                  <WOCreationLogV2 />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manufacturing/v2/scrap"
            element={
              <ProtectedRoute requiredPage="scrap-v2">
                <ManufacturingDataProvider>
                  <ScrapV2 />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manufacturing/v2/shortages"
            element={
              <ProtectedRoute requiredPage="material-shortages-v2">
                <ManufacturingDataProvider>
                  <MaterialShortagesV2 />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manufacturing/v2/capacity"
            element={
              <ProtectedRoute requiredPage="capacity-planning-v2">
                <ManufacturingDataProvider>
                  <CapacityPlanningV2 />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manufacturing/v2/dashboard"
            element={
              <ProtectedRoute requiredPage="manufacturing-dashboard-v2">
                <ManufacturingDataProvider>
                  <ManufacturingDashboardV2 />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manufacturing/v2/kpis"
            element={
              <ProtectedRoute requiredPage="kpi-dashboard-v2">
                <ManufacturingDataProvider>
                  <KPIDashboardV2 />
                </ManufacturingDataProvider>
              </ProtectedRoute>
            }
          />

          {/* Capacity Planning */}
          <Route
            path="/capacity-planning"
            element={
              <ProtectedRoute requiredPage="capacity-planning">
                <CapacityPlanning />
              </ProtectedRoute>
            }
          />

          {/* Stock Procurement */}
          <Route
            path="/stock-procurement"
            element={
              <ProtectedRoute requiredPage="stock-procurement">
                <StockProcurement />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/scripts"
            element={
              <ProtectedRoute adminOnly>
                <AdminScripts />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/backups"
            element={
              <ProtectedRoute adminOnly requiredPage="backups">
                <Backups />
              </ProtectedRoute>
            }
          />
          
          <Route 
            path="/audit" 
            element={
              <ProtectedRoute adminOnly requiredPage="audit">
                <Box sx={{ p: 4 }}>
                  <Typography variant="h4">Audit Logs (Coming Soon)</Typography>
                </Box>
              </ProtectedRoute>
            } 
          />
          
          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Box>
      
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ConnectionProvider>
        <ConnectionHandler />
        <AuthProvider>
          <Router>
            <AppRoutes />
          </Router>
        </AuthProvider>
      </ConnectionProvider>
    </ThemeProvider>
  );
}

// Component to connect the API error handler to the ConnectionContext
function ConnectionHandler() {
  const { setConnectionError } = useConnection();
  
  useEffect(() => {
    setConnectionErrorHandler(setConnectionError);
  }, [setConnectionError]);
  
  return null;
}

export default App;
